import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { gql, useMutation } from '@apollo/client';
import { CsvTemplatesModel, CsvTemplateModel } from '@utmost.co/enterprise-models';
import { Icon, Dialog, Button, Typography } from '@beeline.com/bp-design-system';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { useQuickStartIntl } from './translations/useQuickStartIntl';
const DELETE_CSV_TEMPLATE = gql`
  mutation DeleteCsvTemplate($uid: String!) {
    deleteCsvTemplate(uid: $uid) {
      uid
      name
    }
  }
`;
interface RemoveTemplateProps {
  onClose: () => void;
  onRemove: () => void;
  csvTemplates: CsvTemplatesModel;
}
export const RemoveTemplate: FC<React.PropsWithChildren<RemoveTemplateProps>> = ({
  onClose,
  csvTemplates,
  onRemove
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [removeCsvTemplate, { loading }] = useMutation<
    {
      deleteCsvTemplate: CsvTemplateModel;
    },
    {
      uid: string;
    }
  >(DELETE_CSV_TEMPLATE);
  const intl = useQuickStartIntl();
  const handleRemove = async (uid: string) => {
    const response = await removeCsvTemplate({ variables: { uid } });
    enqueueSnackbar(intl.deleteTemplateSuccess({ name: response.data?.deleteCsvTemplate.name }), {
      variant: 'success'
    });
    onRemove();
  };
  const anyToDelete = useMemo(
    () => csvTemplates.items.filter((item) => !item.default).length !== 0,
    [csvTemplates.items]
  );
  return (
    <Dialog open onClose={onClose} title={intl.removeTemplate}>
      <div>
        {anyToDelete ? (
          <>
            {' '}
            <Typography variant="body1">{intl.chooseTemplateToRemove}</Typography>
            <Typography variant="body2">{intl.defaultsCannotBeRemoved}</Typography>
          </>
        ) : (
          <Typography variant="body1">{intl.noTemplatesToDelete}</Typography>
        )}
        <StyledTemplateList>
          {csvTemplates.items
            .filter((item) => !item.default)
            .map((item) => (
              <StyledFile
                key={item.uid}
                href={`${window.location.origin}/api/media/${item.template.uid}/content`}
                download={item.template.filename}
              >
                {item.template.filename}
                <Icon
                  name="delete"
                  size="small"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleRemove(item.uid);
                  }}
                />
              </StyledFile>
            ))}
        </StyledTemplateList>
        <StyledDialogControl>
          <Button style={{ marginLeft: 'auto' }} onClick={onClose} disabled={loading}>
            {intl.cancel}
          </Button>
        </StyledDialogControl>
      </div>
    </Dialog>
  );
};
export const StyledTemplateList = styled.div`
  max-height: 50vh;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;
export const StyledFile = styled.a`
  padding: 1rem;
  margin: 0.5rem;
  color: #0f66bd;
  border: 1px solid #e9edef;
  border-radius: 5px;
  display: flex;
  align-items: center;
  svg {
    margin-left: auto;
    cursor: pointer;
  }
`;
export const StyledDialogControl = styled.div`
  margin-top: 2rem;
  display: flex;
`;
